import React from 'react'
import { StyleSheet, View } from 'react-native'
import { BtButton, BtLink, BtText } from '@linqpal/components/src/ui'
import { observer } from 'mobx-react'
import { useTranslation } from 'react-i18next'
import ApplicationStore, { EditMode } from '../../ApplicationStore'
import { Spacer } from '../../../../../ui/atoms'
import {
  Compliance,
  InvitationSent,
  LineOfCreditAgreement,
  SubmitButton,
  VerifyEmailAlert,
} from '../../components'
import { commonColors } from '@linqpal/common-frontend/src/theme'
import { useResponsive } from '@linqpal/components/src/hooks'
import RootStore from '../../../../../store'
import {
  IconGrayCheck,
  IconGreenCheck,
  PeopleIconBlue,
} from '../../../../../assets/icons'
import { getGroupTitle } from '../../groupTitles'
import { composeStyle } from '@linqpal/common-frontend/src/helpers'
import { CoOwnerAcceptanceWarning } from '../coOwnerInfo/components/CoOwnerAcceptanceWarning'

function Review(props) {
  const { t } = useTranslation('application')
  const { doc, flowController } = props
  const { sm } = useResponsive()
  const {
    showVerifyEmail,
    setShowVerifyEmail,
    isInvitedOwner,
    hasOwnerPersonalInformation,
    hasSubmissionRights,
    authorisedInvitee,
  } = ApplicationStore

  const flowSections = flowController.getGroupNames()
  const flowSteps = flowController.getFlowSteps()
  const canSubmit = ApplicationStore.canSubmit(flowSteps)

  const onEdit = (section) => {
    flowController.editSection(section, false)
  }

  const CompletedItem = ({ section }) => {
    return (
      <View style={styles.listView}>
        <IconGreenCheck style={{ width: 16, height: 12 }} />
        <BtText style={styles.listText}>
          {t(getGroupTitle(section) as any)}
        </BtText>
        <BtLink
          title={t('Review.Edit')}
          textStyle={styles.linkText}
          onPress={() => onEdit(section)}
        />
      </View>
    )
  }

  const IncompleteItem = ({ section }) => {
    return (
      <View
        style={composeStyle(styles.listView, {
          height: 96,
          paddingTop: 23,
          alignItems: 'flex-start',
        })}
      >
        <IconGrayCheck style={{ width: 16, height: 12, marginTop: 7 }} />
        <View style={styles.childView}>
          <BtText style={composeStyle(styles.listText, { marginLeft: 0 })}>
            {t(getGroupTitle(section) as any)}
          </BtText>
          <BtText style={styles.warningText}>
            {t('Review.IncompleteSectionText')}
          </BtText>
        </View>
        <BtLink
          title={t('Review.Edit')}
          textStyle={styles.linkText}
          onPress={() => onEdit(section)}
        />
      </View>
    )
  }

  const PersonalInformationPrompt = () => {
    const onPress = () => {
      ApplicationStore.setEditMode(EditMode.SKIP_FILLED_STRICT)
      flowController.editSection('businessOwner', false)
    }

    return (
      <View
        style={{
          backgroundColor: commonColors.backgroundColor,
          borderRadius: 12,
          padding: 16,
        }}
      >
        <View style={{ flexDirection: 'row' }}>
          <PeopleIconBlue style={{ height: 18, width: 18, marginTop: 4 }} />
          <BtText
            t={t}
            sm={sm}
            canSubmit={canSubmit}
            style={{
              fontSize: 16,
              marginLeft: 12,
              marginBottom: 12,
            }}
          >
            {t('Review.PersonalInformationPrompt')}
          </BtText>
        </View>
        <View>
          <BtButton
            size={'small'}
            style={{ marginLeft: 30, maxWidth: 217 }}
            onPress={onPress}
          >
            {t('Review.CompleteApplication')}
          </BtButton>
        </View>
      </View>
    )
  }

  return (
    <>
      <Spacer height={22} />
      {isInvitedOwner && !hasOwnerPersonalInformation && (
        <PersonalInformationPrompt />
      )}
      {flowSections.map((section) => {
        const steps = flowController.getGroupSteps(section)
        return steps.length ? (
          ApplicationStore.allQuestionsAnswered(steps) ? (
            <CompletedItem key={section} section={section} />
          ) : (
            <IncompleteItem key={section} section={section} />
          )
        ) : null
      })}
      <Spacer height={12} />
      {!hasSubmissionRights && authorisedInvitee?.invited && (
        <>
          <Spacer height={17} />
          <InvitationSent document={doc} />
        </>
      )}

      {ApplicationStore.isCreditApplication && ApplicationStore.hasCoOwners ? (
        <CoOwnerAcceptanceWarning style={{ marginTop: 20 }} />
      ) : null}

      {sm && (
        <View style={{ alignItems: 'center' }}>
          {ApplicationStore.hasSubmissionRights && (
            <>
              <Compliance
                t={t}
                sm={sm}
                canSubmit={canSubmit}
                style={{
                  marginBottom: 12,
                  backgroundColor: '#FFF',
                }}
              />
              <SubmitButton t={t} canSubmit={canSubmit} />
            </>
          )}
          <BtLink
            disabled={RootStore.isBusy}
            title={t('Review.ReviewButton')}
            textStyle={
              RootStore.isBusy ? styles.disabledLinkText : styles.linkText
            }
            onPress={() => doc.setSection('review.preview')}
            testID="ApplicationReviewButton"
          />
        </View>
      )}
      <VerifyEmailAlert close={showVerifyEmail} setClose={setShowVerifyEmail} />
      {ApplicationStore.isCreditApplication && <LineOfCreditAgreement />}
    </>
  )
}

export default {
  title: 'Review.Heading',
  canSkip: false,
  canMoveNext: false,
  showFooterMessage: true,
  component: observer(Review),
}

const styles = StyleSheet.create({
  listView: {
    height: 76,
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomColor: '#CCD6DD',
    borderBottomWidth: 1,
  },
  listText: {
    marginLeft: 24,
    fontWeight: '600',
    fontSize: 20,
    lineHeight: 26,
    color: '#335C75',
    flex: 1,
  },
  linkText: {
    color: commonColors.accentText,
    fontWeight: '600',
    fontSize: 16,
    lineHeight: 24,
  },
  childView: {
    flex: 1,
    marginLeft: 15,
  },
  warningText: {
    marginTop: 9,
    color: '#FE8134',
    fontWeight: '500',
    fontSize: 14,
    lineHeight: 16,
  },
  disabledLinkText: {
    color: '#99ADBA',
    fontWeight: '700',
    fontSize: 16,
    lineHeight: 24,
  },
})
